import React, { useState, useMemo } from 'react';
import { useCampaign } from '../../contexts/CampaignContext';
import { INVENTORY_CATALOG, INVENTORY_CATEGORIES } from '../../lib/inventory';

// DM modal: grant any catalog item to a player, free of charge. Relic items
// (one-shot rewards flagged `relic: true` in the catalog) are pinned to the
// top of the picker; the rest of the catalog follows grouped by category.
export default function GrantItemPanel({ initialPlayerId = null, onClose }) {
  const { players, grantItem } = useCampaign();
  const [playerId, setPlayerId] = useState(initialPlayerId || '');
  const [itemName, setItemName] = useState('');
  const [busy,     setBusy]     = useState(false);
  const [result,   setResult]   = useState(null); // { ok, text }

  const playerList = useMemo(
    () => Object.values(players || {}).sort((a, b) => (a.charName || a.name || '').localeCompare(b.charName || b.name || '')),
    [players]
  );
  const relics = useMemo(() => INVENTORY_CATALOG.filter((i) => i.relic), []);
  const catIcon = (id) => INVENTORY_CATEGORIES.find((c) => c.id === id)?.icon || '';
  const selected = INVENTORY_CATALOG.find((i) => i.name === itemName) || null;

  const doGrant = async () => {
    if (!playerId || !itemName || busy) return;
    setBusy(true);
    setResult(null);
    const res = await grantItem(playerId, itemName);
    setBusy(false);
    const target = players?.[playerId];
    setResult(res.ok
      ? { ok: true,  text: `Granted ${itemName} to ${target?.charName || target?.name || 'player'}.` }
      : { ok: false, text: res.reason || 'Grant failed.' });
  };

  return (
    <div style={styles.backdrop} onClick={onClose}>
      <div style={styles.panel} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <span>🎁 Grant Item</span>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <label style={styles.label}>Player</label>
        <select style={styles.select} value={playerId} onChange={(e) => { setPlayerId(e.target.value); setResult(null); }}>
          <option value="">— pick a player —</option>
          {playerList.map((p) => (
            <option key={p.id} value={p.id}>{p.charName || p.name}</option>
          ))}
        </select>

        <label style={styles.label}>Item</label>
        <select style={styles.select} value={itemName} onChange={(e) => { setItemName(e.target.value); setResult(null); }}>
          <option value="">— pick an item —</option>
          {relics.length > 0 && (
            <optgroup label="★ Relics">
              {relics.map((i) => <option key={i.name} value={i.name}>{catIcon(i.category)} {i.name}</option>)}
            </optgroup>
          )}
          {INVENTORY_CATEGORIES.map((cat) => {
            const items = INVENTORY_CATALOG.filter((i) => i.category === cat.id && !i.relic);
            if (items.length === 0) return null;
            return (
              <optgroup key={cat.id} label={`${cat.icon} ${cat.label}`}>
                {items.map((i) => <option key={i.name} value={i.name}>{i.name}</option>)}
              </optgroup>
            );
          })}
        </select>

        {selected && (
          <div style={styles.preview}>
            {selected.relic ? '★ ' : ''}{selected.description}
            {selected.weightLb ? <span style={styles.weight}> · {selected.weightLb} lb</span> : null}
          </div>
        )}

        <button
          style={{ ...styles.grantBtn, ...((!playerId || !itemName || busy) ? styles.grantBtnDisabled : {}) }}
          disabled={!playerId || !itemName || busy}
          onClick={doGrant}
        >
          {busy ? 'Granting…' : '🎁 Grant'}
        </button>

        {result && (
          <div style={{ ...styles.result, color: result.ok ? '#4ade80' : '#f87171' }}>
            {result.text}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(5,8,18,0.6)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  panel:    { width: 340, maxWidth: 'calc(100vw - 32px)', background: '#1a1a2e', border: '1px solid #333355', borderRadius: 10, padding: 16, display: 'flex', flexDirection: 'column', gap: 8, boxShadow: '0 12px 40px rgba(0,0,0,0.6)' },
  header:   { display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#e2e2f0', fontWeight: 700, fontSize: 15, marginBottom: 4 },
  closeBtn: { background: 'transparent', border: 'none', color: '#888aaa', fontSize: 16, cursor: 'pointer', padding: 4 },
  label:    { color: '#888aaa', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 },
  select:   { background: '#242440', color: '#e2e2f0', border: '1px solid #333355', borderRadius: 6, padding: '8px 10px', fontSize: 13 },
  preview:  { background: '#242440', border: '1px solid #333355', borderRadius: 6, padding: '8px 10px', color: '#b8b8d0', fontSize: 12, lineHeight: 1.4 },
  weight:   { color: '#888aaa' },
  grantBtn: { marginTop: 6, background: '#2a4a2a', color: '#bbf7d0', border: '1px solid #4ade80', borderRadius: 8, padding: '10px 0', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  grantBtnDisabled: { opacity: 0.45, cursor: 'default' },
  result:   { fontSize: 12.5, textAlign: 'center', minHeight: 16 },
};
